import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { withAreaPrefix } from '../../../../../lib/area-routes';

import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roleApi } from '../../../../../lib/api/role.api';
import { permissionApi } from '../../../../../lib/api/permission.api';
import type { RoleInput, PermissionRecord } from '@cms/models';
import { STATUS_LOWER } from '@cms/utils';
import { Toggle } from '../../../../../components/ui/toggle';
import { NativeButtonWithIcon } from '@/components/common/NativeButtonWithIcon';

function groupPermissionsByModule(
  permissions: PermissionRecord[]
): Map<string, PermissionRecord[]> {
  const map = new Map<string, PermissionRecord[]>();
  for (const p of permissions) {
    const list = map.get(p.module) ?? [];
    list.push(p);
    map.set(p.module, list);
  }
  return map;
}

export function RoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toAreaPath = (path: string): string => withAreaPrefix(path, location.pathname);
  const queryClient = useQueryClient();
  const isEdit = id !== 'new';

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ['role', id],
    queryFn: () => roleApi.getById(id!),
    enabled: isEdit,
  });

  const {
    data: permissions = [],
    isError: permissionsLoadFailed,
    error: permissionsError,
    isLoading: permissionsLoading,
  } = useQuery({
    queryKey: ['permissions'],
    queryFn: permissionApi.getAll,
  });

  const permissionsByModule = useMemo(() => groupPermissionsByModule(permissions), [permissions]);

  const createMutation = useMutation({
    mutationFn: (data: RoleInput) => roleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      navigate(toAreaPath('/settings/roles'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<RoleInput>) => roleApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['role', id] });
      navigate(toAreaPath('/settings/roles'));
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<Omit<RoleInput, 'status'> & { status?: 'active' | 'inactive' }>({
    defaultValues: {
      name: '',
      description: '',
      scopes: [],
      isSystem: false,
      status: 'active',
    },
  });

  useEffect(() => {
    if (role) {
      reset({
        name: role.name,
        description: role.description ?? undefined,
        scopes: role.scopes ?? [],
        isSystem: role.isSystem,
        status:
          role.status?.toLowerCase() === STATUS_LOWER.INACTIVE
            ? STATUS_LOWER.INACTIVE
            : STATUS_LOWER.ACTIVE,
      });
    }
  }, [role, reset]);

  const selectedScopes = watch('scopes') || [];

  const toggleScope = (scope: string) => {
    const currentScopes = selectedScopes;
    if (currentScopes.includes(scope)) {
      setValue(
        'scopes',
        currentScopes.filter((s: string) => s !== scope)
      );
    } else {
      setValue('scopes', [...currentScopes, scope]);
    }
  };

  const onSubmit = async (data: Omit<RoleInput, 'status'> & { status?: 'active' | 'inactive' }) => {
    if (!data.scopes?.length) {
      alert('Please select at least one scope');
      return;
    }
    const payload = { ...data, status: (data.status ?? 'active').toLowerCase() } as RoleInput;
    if (isEdit) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-2">
      <div className="flex items-center justify-between">
        {roleLoading ? (
          <div className="h-9 flex items-center text-muted-foreground">Loading role...</div>
        ) : (
          <>
            <h1 className="text-3xl font-bold">{isEdit ? 'Edit Role' : 'Create Role'}</h1>
            <NativeButtonWithIcon
              type="button"
              onClick={() => navigate(toAreaPath('/settings/roles'))}
              icon="x"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </NativeButtonWithIcon>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="glass-card p-4 space-y-4">
        {roleLoading && isEdit ? (
          <div className="flex justify-center items-center h-64">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="animate-spin text-primary"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="role-name" className="block text-sm font-medium text-foreground">
                Name
              </label>
              <input
                id="role-name"
                {...register('name', {
                  required: 'Name is required',
                  maxLength: { value: 100, message: 'Name must be less than 100 characters' },
                })}
                type="text"
                className="mt-1 block w-full px-3 py-2 bg-background border border-border rounded-md focus:ring-2 focus:ring-primary/20"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="role-description"
                className="block text-sm font-medium text-foreground"
              >
                Description
              </label>
              <textarea
                id="role-description"
                {...register('description')}
                rows={3}
                className="mt-1 block w-full px-3 py-2 bg-background border border-border rounded-md focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Toggle
                    id="role-status"
                    label="Status"
                    checked={(field.value ?? STATUS_LOWER.ACTIVE) === STATUS_LOWER.ACTIVE}
                    onCheckedChange={checked =>
                      field.onChange(checked ? STATUS_LOWER.ACTIVE : STATUS_LOWER.INACTIVE)
                    }
                  />
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="role-is-system"
                type="checkbox"
                {...register('isSystem')}
                className="h-4 w-4 rounded border-gray-400 text-primary focus:ring-primary"
              />
              <label htmlFor="role-is-system" className="text-sm font-medium text-foreground">
                Is system role
              </label>
            </div>

            <fieldset aria-labelledby="scopes-legend">
              <legend id="scopes-legend" className="block text-sm font-medium text-foreground mb-2">
                Scopes (Select at least one)
              </legend>
              <div className="border border-border rounded-md p-4 max-h-96 overflow-y-auto space-y-4 bg-background/50">
                {permissionsLoadFailed && (
                  <p className="text-sm text-destructive">
                    Could not load the permission list.{' '}
                    {(permissionsError as { response?: { data?: { message?: string } } })?.response?.data
                      ?.message ?? 'Check your access or try again.'}
                  </p>
                )}
                {!permissionsLoadFailed &&
                  Array.from(permissionsByModule.entries()).map(([module, perms]) => (
                    <div key={module}>
                      <h3 className="font-medium text-foreground mb-2 capitalize">{module}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {perms.map(p => (
                          <label
                            key={p.id}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-muted/20 p-1 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={selectedScopes.includes(p.scope)}
                              onChange={() => toggleScope(p.scope)}
                              className="rounded border-gray-400 text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-foreground">{p.scope}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                {!permissionsLoadFailed && permissionsLoading && (
                  <p className="text-sm text-muted-foreground">Loading permissions…</p>
                )}
                {!permissionsLoadFailed && !permissionsLoading && permissions.length === 0 && (
                  <p className="text-sm text-muted-foreground">No permissions loaded.</p>
                )}
              </div>
              {selectedScopes.length === 0 && (
                <p className="mt-1 text-sm text-destructive">Please select at least one scope</p>
              )}
            </fieldset>

            {isEdit && role?.isSystem && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 px-4 py-3 rounded">
                This is a system role. You can edit its details and permissions here; it cannot be deleted
                from the roles list.
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <NativeButtonWithIcon
                type="button"
                onClick={() => navigate(toAreaPath('/settings/roles'))}
                icon="x"
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-foreground"
              >
                Cancel
              </NativeButtonWithIcon>
              <NativeButtonWithIcon
                type="submit"
                icon={isEdit ? 'circle-check' : 'plus'}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors shadow-sm"
              >
                {isEdit ? 'Update' : 'Create'}
              </NativeButtonWithIcon>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
